/**
 * billing.ts — types, money/date formatting and every customer-facing string
 * for the pricing UI. One module on purpose: the copy-lint test
 * (components/billing/__tests__/copyLint.test.tsx) reads it, and the design's
 * voice rules (DESIGN.md "Never, in the product layer", plus the money
 * carve-out) are easiest to hold when every sentence lives in one place.
 *
 * Money (P-DISPLAY, pricing plan 2026-10-07): a plan or trial allowance is
 * shown as a PERCENT of this month ("38% used"), with any credit beyond it
 * as "+12% extra"; the server computes both (balance.usage, lib/usageView)
 * and this file only words them. Dollars appear for top-ups ("$7.40 top-up",
 * at the price paid), plan prices ("$59 / month") and hosting ("$18/month").
 * The allowance itself is never a dollar figure, here or anywhere. A
 * per-step or per-call cost never appears. The unit is "building".
 */

import { PLANS_SRC, lifecycleSrc, plansHref as plansLink, refusalSrc, withPlansSrc } from './plansEntry';

// ── Server shapes (server/routes/billing.js, lib/pauseCards.js) ─────────────

export interface BillingPlan {
  id: string;
  displayName: string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface Entitlements {
  canPublish: boolean;
  customDomains: boolean;
  publishKinds?: string[];
  hostedKinds?: string[];
  saasTemplates?: boolean;
  activeSaasLimit?: number | null;
  projectCap?: number | null;
  zipDownload?: boolean;
  badge?: boolean;
}

export interface BillingOffer {
  id: string;
  displayName: string;
  priceUsd: number;
  interval: string;
  monthlyGrantMicros: number;
  entitlements?: Entitlements;
}

/** server/services/projectLifecycleService.js dates, as the balance route exposes them. */
export interface LifecycleInfo {
  /** Published full apps pause then (plan canceling / past due / ended), or null. */
  appsPauseAt: string | null;
  /** Trial-published landing pages pause then (trial ended), or null. */
  pagePauseAt: string | null;
  /** Projects currently paused in the org. */
  pausedCount: number;
}

/** server/lib/usageView.js: the ONE place the balance becomes what we show. */
export type ExtraKind = 'rollover' | 'topup' | 'referral_earned' | 'referral_received' | 'promo' | 'free'
  | 'admin' | 'beta'; // older servers only; both read as promo credit
/** The soonest end within one kind: when, and how much of it goes then (in the row's own units). */
export interface ExtraEnding {
  at: string;
  micros: number;
  pct: number;
  retailCents?: number;
  usdCents?: number;
}
export interface ExtraSource {
  kind: ExtraKind;
  remainingMicros: number;
  amountMicros: number;
  /** What is left, as a percent of the main row's 100% (the plan month, or the trial grant). */
  pct: number;
  expiresAt: string | null;
  ending?: ExtraEnding | null;
  /** Top-ups: what is left and what was bought, at the price paid. */
  retailCents?: number;
  purchasedRetailCents?: number;
  /** What is left in dollars: a top-up at the price paid, the rest at cost (a referral reward's dollars). Absent from older servers. */
  usdCents?: number;
}
export interface ExtraCredit {
  remainingMicros: number;
  amountMicros: number;
  pct: number;
  /** What is left in dollars, every source added up; absent from older servers. */
  usdCents?: number;
  /** 0-1 of the extra credit spent. */
  usedShare: number;
  bySource: ExtraSource[];
}

export interface Usage {
  basis: 'plan' | 'trial' | 'credit' | 'unmetered' | null;
  /** Percent of this month's allowance used, rounded UP; null = no bar. */
  planPctUsed: number | null;
  /** Everything else unexpired that is not a top-up, as a percent of the same month. */
  carriedPct: number | null;
  carriedMicros: number;
  planMonthMicros: number | null;
  currentPeriodEnd: string | null;
  currentGrantId: string | null;
  topupMicros: number;
  /** Top-up left at the price paid, in cents. */
  topupRetailCents: number;
  /** What the top-ups with credit left came to when bought, in cents (absent from older servers). */
  topupPurchasedRetailCents?: number;
  /** Every spendable grant that is not the main row, by kind, in spending order (absent from older servers). */
  extra?: ExtraCredit | null;
  /** Which credit the next build draws from first, by the ledger's order; absent from older servers. */
  spendingNow?: 'plan' | 'free' | ExtraKind | null;
  topupExpiresAt?: string | null;
}

export type HostingTier = 'sleep' | 'always-on';

/** One tier's hosting slots (server hostingBillingService.slotView). */
export interface HostedProject {
  id: string;
  name: string;
}

export interface SlotCount {
  /** Slots that renew (Stripe's quantity). */
  owned: number;
  /** Live apps holding one. */
  used: number;
  /** Slots no app holds, ending ones included: publishing uses one at no charge. */
  free: number;
  /** Removed this period: usable until periodEnd, then gone. */
  ending: number;
  priceUsd: number;
  /** The projects using slots on this tier. */
  projects?: HostedProject[];
}

export interface HostingSlots {
  periodEnd: string | null;
  sleep: SlotCount;
  'always-on': SlotCount;
}

export interface Hosting {
  /** Slots on the $9/mo "Sleep when idle" line. */
  quantity: number;
  priceUsd: number;
  /** Slots on the $18/mo "Always on" line (absent from older servers). */
  alwaysOn?: { quantity: number; priceUsd: number };
  /** Both lines together. */
  monthlyCents: number;
  /** Per-tier slots; null when the plan does not bill hosting now, absent on older servers. */
  slots?: HostingSlots | null;
}

/**
 * What publishing on a tier does to the bill (prod-status tierSlots):
 * 'free' a slot the team owns is free · 'paid' a slot removed this period is
 * reused · 'covered' a live Always on app moving to Sleep · 'buy' a new
 * slot, charged now · 'current' the tier the live app is on.
 */
export type SlotOutcome = 'free' | 'paid' | 'covered' | 'buy' | 'current';
export interface TierSlots {
  periodEnd: string | null;
  outcome: Partial<Record<HostingTier, SlotOutcome>> | null;
}

export const TIER_NAME: Record<HostingTier, string> = { sleep: 'Sleep when idle', 'always-on': 'Always on' };
/** "a Sleep when idle slot" / "an Always on slot". */
const aSlot = (t: HostingTier): string => `${t === 'always-on' ? 'an' : 'a'} ${TIER_NAME[t]} slot`;

export interface Limits {
  activeProjects: number;
  projectCap: number | null;
  activeSaas: number;
  saasCap: number | null;
}

export type GateMode = 'off' | 'shadow' | 'enforce';

export interface Balance {
  orgId: string;
  orgName: string;
  isPersonal: boolean;
  isAdmin: boolean;
  admins: string[];
  unmetered: boolean;
  spendableMicros: number;
  activeGrantMicros: number;
  nextExpiry: string | null;
  expiringSoon: boolean;
  low: boolean;
  plan: BillingPlan | null;
  /** free | trial | starter | pro | business (server/config/plans.js tierFor). */
  tier?: string;
  entitlements: Entitlements;
  limits?: Limits;
  /** Absent only on an older server: then no percent, no bar, no dollars. */
  usage?: Usage;
  hosting?: Hosting;
  /** T11/DT7: when the org's published apps (plan ending) or trial page (trial ended) pause. */
  lifecycle?: LifecycleInfo | null;
  /** Decision 9: the first paid invoice took the badge off the live trial pages. */
  badge?: { removedAt: string; pages: number } | null;
  catalog?: BillingOffer[];
  /** Added in T13; absent on an older server, which reads as 'off'. */
  gate?: GateMode;
  expiredAt?: string | null;
  offer?: BillingOffer | null;
  /** A top-up can be bought: anyone metered, with or without a plan (founder 2026-11-05). Absent on an older server. */
  canTopUp?: boolean;
  /**
   * Grant sources (free, promo, beta, plan, admin). Absent on an older
   * server, which reads as free-only (the copy before sources existed).
   */
  activeSources?: string[];
  grantSources?: string[];
  expiredSource?: string | null;
  /** The 24-hour template trial (trial plan; server routes/billing.js
   *  templateTrialView). Null off the trial plan once never taken; absent on
   *  an older server. */
  templateTrial?: TemplateTrialView | null;
}

export interface TemplateTrialView {
  /** The trial can still be started: on the trial plan and not used. */
  available: boolean;
  /** A card is saved and checked: the next SaaS template start takes the trial. */
  cardOnFile: boolean;
  /** Taken (running, ended or converted): no second one. */
  used: boolean;
  status: 'card' | 'active' | 'ended' | 'converted' | null;
  projectId: string | null;
  endsAt: string | null;
}

export type PauseReason = 'exhausted' | 'expired' | 'plan_exhausted';
export type PauseState = 'paused' | 'processing' | 'ready' | 'used';

export interface PauseCardData {
  id: string;
  taskId: string | null;
  messageId: string | null;
  source: 'preflight' | 'midturn' | 'background';
  reason: PauseReason;
  stoppedStep: string | null;
  workSaved: boolean;
  state: PauseState;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// ── Visibility ──────────────────────────────────────────────────────────────

/**
 * Should the customer see ANY pricing chrome for this balance?
 *
 * Hidden while the gate is off (today, for every paying customer) and in
 * shadow (metering only, never blocking) except for our own staff, so
 * staging can be dogfooded. Always hidden for contract (unmetered) orgs —
 * they are billed out of band and a balance would be a false statement.
 */
export function billingVisible(b: Balance | null | undefined, user?: { isInternal?: boolean } | null): boolean {
  if (!b || b.unmetered) return false;
  if (b.gate === 'enforce') return true;
  return b.gate === 'shadow' && !!(user && user.isInternal);
}

export function isSubscribed(b: Balance | null | undefined): boolean {
  if (!b || !b.plan) return false;
  return ['active', 'trialing', 'past_due'].includes(b.plan.status);
}

/**
 * A top-up can be bought: the org is metered, with or without a plan (founder
 * 2026-11-05, reversing 2026-10-14). The server's flag wins; an older server
 * without it reads as subscribed, the rule it enforces.
 */
export function canTopUp(b: Balance | null | undefined): boolean {
  if (!b || b.unmetered) return false;
  return typeof b.canTopUp === 'boolean' ? b.canTopUp : isSubscribed(b);
}

export function isOut(b: Balance | null | undefined): boolean {
  return !!b && !b.unmetered && (b.spendableMicros || 0) <= 0;
}

// ── What kind of credit ─────────────────────────────────────────────────────
// "Free" is said only when it is true: a team that redeemed a $50 code has
// building credit, not "your free $6".

const onlyFree = (sources: string[] | undefined): boolean =>
  !sources || sources.length === 0 || sources.every((x) => x === 'free');

/**
 * The label for credit the org can still spend: "Free credit" or "Building
 * credit". With nothing left to spend, it is named by what the org had: a
 * spent free grant is still "Free credit", and an org that never had a grant
 * (a team, a staff org, an unverified signup) never reads "Free credit".
 */
export function creditLabel(b: Balance): string {
  if (b.activeSources && b.activeSources.length > 0) return onlyFree(b.activeSources) ? 'Free credit' : 'Building credit';
  return onlyEverFree(b) ? 'Free credit' : 'Building credit';
}

/**
 * Every grant the org ever had was the free one. An org with no grant at all
 * is not "free" (onlyFree([]) is vacuously true, so it cannot answer this).
 * An older server sends no grantSources and keeps the original free copy.
 */
function onlyEverFree(b: Balance): boolean {
  if (!Array.isArray(b.grantSources)) return true;
  return b.grantSources.length > 0 && onlyFree(b.grantSources);
}

/** The credit that ended by expiring was the free grant. */
function expiredWasFree(b: Balance): boolean {
  return b.expiredSource == null ? onlyFree(b.grantSources) : b.expiredSource === 'free';
}

/** "Free trial ended" or "Building credit ended", with the date when known (P-DISPLAY: the same words on every surface). */
export function endedLine(b: Balance, { withDate = true, sentence = false }: { withDate?: boolean; sentence?: boolean } = {}): string {
  const d = withDate && b.expiredAt ? shortDate(b.expiredAt) : '';
  const base = expiredWasFree(b)
    ? (sentence ? 'Your free trial ended' : 'Free trial ended')
    : (sentence ? 'Your building credit ended' : 'Building credit ended');
  return d ? `${base} on ${d}` : base;
}

/** Credit ran out by expiring, not by being spent (design D12). */
export function isFreeExpired(b: Balance | null | undefined): boolean {
  return !!b && isOut(b) && !isSubscribed(b) && !!b.expiredAt;
}

// ── Formatting ──────────────────────────────────────────────────────────────

/** "$7.40". Floors to the cent so a balance is never overstated. */
export function formatUsd(micros: number): string {
  const cents = Math.max(0, Math.floor((micros || 0) / 10_000));
  return `$${(cents / 100).toFixed(2)}`;
}

/** "$50" for whole dollars, "$7.40" otherwise: amounts added, plan prices. */
export function formatUsdShort(micros: number): string {
  const cents = Math.max(0, Math.floor((micros || 0) / 10_000));
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** Screen-reader amount: "7 dollars 40", "80 cents", "10 dollars". */
export function spokenUsd(micros: number): string {
  const cents = Math.max(0, Math.floor((micros || 0) / 10_000));
  const d = Math.floor(cents / 100);
  const c = cents % 100;
  if (d === 0) return `${c} cent${c === 1 ? '' : 's'}`;
  const dollars = `${d} dollar${d === 1 ? '' : 's'}`;
  return c === 0 ? dollars : `${dollars} ${c}`;
}

function toDate(iso: string | Date | null | undefined): Date | null {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Oct 28" */
export function shortDate(iso: string | Date | null | undefined): string {
  const d = toDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

/** "October 28", for aria-labels. */
export function longDate(iso: string | Date | null | undefined): string {
  const d = toDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : '';
}

/** Whole days until `iso`, rounded up; 0 once it has passed. */
export function daysUntil(iso: string | null | undefined, now: number = Date.now()): number {
  const d = toDate(iso);
  if (!d) return 0;
  return Math.max(0, Math.ceil((d.getTime() - now) / DAY_MS));
}

/** "ends today" | "ends tomorrow" | "ends in N days", from daysUntil. */
export function endsIn(days: number): string {
  if (days <= 0) return 'ends today';
  if (days === 1) return 'ends tomorrow';
  return `ends in ${days} days`;
}

/** "Acme team", never "Acme team team". */
export function teamLabel(orgName: string): string {
  const n = (orgName || '').trim();
  if (!n) return 'Your team';
  return /\bteam$/i.test(n) ? n : `${n} team`;
}

// ── Balance row / rail ring / mobile strip (design decisions 2, 3; D7, D12, D15) ──

export interface BalanceView {
  /** "38% used" | "Trial ended" | "Acme team · 38% used" | "$10.00 top-up" */
  headline: string;
  /** "+12% extra · $7.40 top-up" | "Pro · renews Nov 3" | "Free trial · ends Oct 28" | "May run out during your next build" */
  detail: string;
  /** The action word at the end of the detail line. */
  cta: 'Get more' | 'See plans';
  /** Amber (#fbbf24). Never colour-only: `detail` carries the words. */
  warn: boolean;
  /** 0..1, the USED share of this month: the bar and the ring. */
  fill: number;
  /** The bar is this month's plan only; no month, no bar. */
  showBar: boolean;
  /** The full sentence for aria-label (a11y pass 6). */
  ariaLabel: string;
  /** Collapsed-rail tooltip: "38% used · +12% extra". */
  tooltip: string;
  /** Mobile strip text (only shown when low or out). */
  stripText: string;
  /** Show the 44px mobile strip at all. */
  showStrip: boolean;
}

/** "$7.40" from cents (top-ups are the one dollar figure on a balance). */
export function formatCents(cents: number): string {
  const c = Math.max(0, Math.floor(cents || 0));
  return `$${(c / 100).toFixed(2)}`;
}

function spokenCents(cents: number): string {
  return spokenUsd(Math.max(0, Math.floor(cents || 0)) * 10_000);
}

/** The month's end for the row: the plan's renewal, or the trial's end. */
function periodEndOf(b: Balance): string | null {
  if (b.plan && isSubscribed(b)) return b.plan.currentPeriodEnd;
  return (b.usage && b.usage.currentPeriodEnd) || b.nextExpiry || null;
}

/** What the percent is a percent OF, for the spoken sentence. */
function spokenBasis(b: Balance): string {
  const basis = b.usage ? b.usage.basis : null;
  if (basis === 'trial') return 'your free trial';
  if (basis === 'credit') return 'your building credit';
  return "this month's building";
}

/**
 * The non-zero extras. With the server's breakdown (`usage.extra`) it is one
 * figure, the credit meter's: "$13.35 of extra credits", everything spendable
 * beyond this month's plan, top-ups at the price paid and the rest at cost
 * (Aima, 2026-10-20: as a percent of the month, a $1,000 top-up read
 * "+1809%"). The Plans card once said "+23% extra · $10.00 top-up" a few
 * inches from the meter's own figure, the same word for two numbers. On the
 * 'credit' basis the extra IS the 100%, so only a top-up is said; an older
 * server without the breakdown keeps the old split, "+12% extra", "$7.40
 * top-up"; one without dollars, the percent.
 */
function extraParts(u: Usage | undefined): string[] {
  if (!u) return [];
  if (u.extra && u.basis !== 'credit') {
    if (u.extra.remainingMicros <= 0) return [];
    return [u.extra.usdCents != null ? `${formatCents(u.extra.usdCents)} of ${COPY.extraCredits.toLowerCase()}` : `+${u.extra.pct}% ${COPY.extraCredits.toLowerCase()}`];
  }
  const parts: string[] = [];
  if ((u.carriedPct || 0) > 0) parts.push(`+${u.carriedPct}% extra`);
  if ((u.topupRetailCents || 0) > 0) parts.push(`${formatCents(u.topupRetailCents)} top-up`);
  return parts;
}

function spokenExtras(u: Usage | undefined): string {
  if (!u) return '';
  if (u.extra && u.basis !== 'credit') {
    if (u.extra.remainingMicros <= 0) return '';
    return u.extra.usdCents != null ? `, plus ${formatCents(u.extra.usdCents)} of extra credits` : `, plus ${u.extra.pct} percent extra credits`;
  }
  const carried = (u.carriedPct || 0) > 0 ? `${u.carriedPct} percent extra` : '';
  const topup = (u.topupRetailCents || 0) > 0 ? `${formatCents(u.topupRetailCents)} of top-up` : '';
  if (carried && topup) return `, plus ${carried} and ${topup}`;
  if (carried) return `, plus ${carried}`;
  if (topup) return `, plus ${topup}`;
  return '';
}

export function balanceView(b: Balance, now: number = Date.now()): BalanceView {
  const u = b.usage;
  const team = !b.isPersonal ? `${teamLabel(b.orgName)} · ` : '';
  const spokenTeam = !b.isPersonal ? `${teamLabel(b.orgName)}: ` : '';
  const subscribed = isSubscribed(b);
  const out = isOut(b);
  // "Get more" opens the top-up sheet, for a plan holder who can buy one. With
  // no plan it stays "See plans": that page offers the plans and the top-up
  // together, where the sheet alone would hide the plans from every trial.
  const ctaWord: 'Get more' | 'See plans' = canTopUp(b) && subscribed ? 'Get more' : 'See plans';

  // The trial (or other credit) ended by date, nothing else to spend (D12).
  // Never a percent, never "$0.00".
  if (isFreeExpired(b) && !(u && (u.topupRetailCents || 0) > 0)) {
    const ended = expiredWasFree(b) ? 'Trial ended' : 'Building credit ended';
    const spoken = expiredWasFree(b) ? 'Your free trial ended' : 'Your building credit ended';
    return {
      headline: `${team}${ended}`,
      detail: '',
      cta: 'See plans',
      warn: true,
      fill: 0,
      showBar: false,
      ariaLabel: `${spokenTeam}${spoken}${b.expiredAt ? ` on ${longDate(b.expiredAt)}` : ''}. See plans.`,
      tooltip: ended,
      stripText: ended,
      showStrip: true,
    };
  }

  // No month to be a percent of (a top-up on the free tier, an org with no
  // credit yet, or an older server): no bar, no percent.
  if (!u || u.planPctUsed == null) {
    const topup = u && (u.topupRetailCents || 0) > 0 ? formatCents(u.topupRetailCents) : null;
    const headline = topup ? `${team}${topup} top-up` : `${team}Building credit`;
    const until = u ? monthYear(u.topupExpiresAt) : '';
    // Bought a year ago and about to lapse: say so, nothing else will.
    const left = u && u.topupExpiresAt ? daysUntil(u.topupExpiresAt, now) : null;
    const ending = !!topup && !out && left != null && left <= 3;
    const endingIn = ending ? endsIn(left as number) : '';
    const detail = ending ? `Top-up ${endingIn}` : topup ? (until ? `Lasts until ${until}` : 'Lasts 12 months') : (out ? 'Nothing left to build with' : '');
    const spoken = ending
      ? `${topup} of top-up left, ${endingIn}`
      : topup ? `${topup} of top-up left, lasts ${until ? `until ${until}` : '12 months'}` : (out ? 'No building credit left' : 'Building credit');
    const warn = out || !!b.low || ending;
    return {
      headline,
      detail: b.low && !out ? 'May run out during your next build' : detail,
      cta: ctaWord,
      warn,
      fill: 0,
      showBar: false,
      ariaLabel: `${spokenTeam}${spoken}. ${ctaWord}.`,
      tooltip: topup ? `${topup} top-up` : 'Building credit',
      stripText: topup ? `${topup} top-up` : 'Building credit',
      showStrip: warn,
    };
  }

  const pct = Math.max(0, Math.min(100, u.planPctUsed));
  const extras = extraParts(u);
  const end = periodEndOf(b);
  const endShort = shortDate(end);
  const endLong = longDate(end);
  const canceling = !!(b.plan && b.plan.cancelAtPeriodEnd);
  const topupLeft = (u.topupRetailCents || 0) > 0;
  // Credit that is ending has to still be there: nextExpiry counts a spent grant,
  // and with only the top-up left that date is not about anything they hold.
  const soon = !subscribed && b.expiringSoon && !!end && !out && !(pct >= 100 && topupLeft);
  // Amber says why in words (D15): the ending, not the top-up line.
  const endingFirst = soon && topupLeft;
  const endWord = subscribed ? (canceling ? 'ends' : 'renews') : 'ends';
  const endPhrase = soon ? endsIn(daysUntil(end, now)) : (endShort ? `${endWord} ${endShort}` : '');
  const spokenEnd = soon ? `, ${endsIn(daysUntil(end, now))}` : (end ? `, ${endWord} ${endLong}` : '');

  // Line 2: the context when there is nothing extra, else the extras.
  const context = u.basis === 'trial'
    ? `Free trial${endPhrase ? ` · ${endPhrase}` : ''}`
    : u.basis === 'credit'
      ? `${creditLabel(b)}${endPhrase ? ` · ${endPhrase}` : ''}`
      : `${b.plan ? b.plan.displayName : 'Plan'}${endPhrase ? ` · ${endPhrase}` : ''}`;
  let detail = extras.length && !endingFirst ? extras.join(' · ') : context;
  let spokenDetail = extras.length && !endingFirst ? spokenExtras(u) : spokenEnd;
  let tooltip = extras.length && !endingFirst ? `${pct}% used · ${extras.join(' · ')}` : `${pct}% used${endPhrase ? ` · ${endPhrase}` : ''}`;

  // Decision 3: the month is gone but a top-up carries on. Normal colour.
  if (pct >= 100 && topupLeft && !out) {
    detail = `Using top-up, ${formatCents(u.topupRetailCents)} left`;
    spokenDetail = `, using top-up, ${formatCents(u.topupRetailCents)} left`;
    tooltip = `100% used · using top-up, ${formatCents(u.topupRetailCents)} left`;
  }

  let warn = !!b.low || out || soon;
  // D15: the amber state says why in words, not only in colour.
  if (b.low && !out) {
    detail = 'May run out during your next build';
    spokenDetail = `${spokenDetail}. May run out during your next build`;
    tooltip = `${pct}% used · may run out during your next build`;
  }

  return {
    headline: `${team}${pct}% used`,
    detail,
    cta: ctaWord,
    warn,
    fill: pct / 100,
    showBar: true,
    ariaLabel: `${spokenTeam}${pct} percent of ${spokenBasis(b)} used${spokenDetail}. ${ctaWord}.`,
    tooltip,
    stripText: pct >= 100 && topupLeft && !out ? `100% used · using top-up, ${formatCents(u.topupRetailCents)} left` : `${pct}% used`,
    showStrip: warn,
  };
}

// ── Pause card (design D8-D12) ──────────────────────────────────────────────

export const SAVED_LINE = 'Your work so far is saved.';
export const KEPT_LINE = "Your changes so far are kept. Once there's credit again, the build finishes the step it stopped on.";

/** D10: "saved" ONLY when the server confirmed the work reached GitHub. */
export function savedLine(card: Pick<PauseCardData, 'workSaved'>): string {
  return card.workSaved ? SAVED_LINE : KEPT_LINE;
}

export function pauseEyebrow(card: PauseCardData): string {
  if (card.state === 'ready') return 'Ready';
  if (card.state === 'processing') return 'Processing';
  return card.source === 'preflight' ? 'Out of credit' : 'Build paused';
}

export function pauseHeading(card: PauseCardData, b: Balance | null): string {
  if (card.state === 'ready') return "You're all set";
  if (card.state === 'processing') return COPY.adding;
  // Refused with credit still left: the next step needed more than remains.
  // "You've used your credit" beside "$1.10 left" in the sidebar is untrue.
  const someLeft = !!b && !b.unmetered && (b.spendableMicros || 0) > 0;
  if (card.reason === 'plan_exhausted') {
    return someLeft ? "You're almost out of this month's building credit" : "You've used this month's building credit";
  }
  if (card.reason === 'expired') {
    // The trial did end, but what stopped this step is a top-up running low.
    if (someLeft && b && b.usage && (b.usage.topupRetailCents || 0) > 0) return "You're almost out of building credit";
    if (b) return endedLine(b, { sentence: true });
    // Not loaded yet: which credit ended is unknown, so never guess "free".
    return 'Your building credit ended';
  }
  if (someLeft) return "You're almost out of building credit";
  if (!b) return "You're out of building credit";
  // Only a team whose credit was ONLY ever the free grant was on a trial.
  if (b && !onlyEverFree(b)) return "You've used your building credit";
  return "You've used your free trial";
}

export function pauseBody(card: PauseCardData, b: Balance | null): string {
  if (card.state === 'ready') return 'Pick up right where the build stopped.';
  if (card.state === 'processing') return COPY.addingBody;
  const saved = savedLine(card);
  // A plan that has ended is no plan, whatever the card recorded when it stopped.
  const planEnded = !!b && !!b.plan && !isSubscribed(b);
  if (card.reason === 'plan_exhausted' && !planEnded) {
    if (b && planCanceling(b)) return `${planEndsLine(b)} ${saved} ${pickPlanTail(b)}`;
    const d = b && b.plan ? shortDate(b.plan.currentPeriodEnd) : '';
    return `${d ? `It renews on ${d}. ` : ''}${saved}`;
  }
  return `${saved} ${keepBuildingLine(b)}`;
}

/** The way back with nothing left to build with. Names a top-up only to someone who can buy one. */
export function keepBuildingLine(b: Balance | null | undefined): string {
  return b && b.isAdmin && canTopUp(b)
    ? 'Add a top-up, pick a plan or enter a code to keep building.'
    : 'Pick a plan or enter a code to keep building.';
}

/**
 * A plan the customer has cancelled still has this month, but it will not
 * renew (review 2026-10-08): "It renews on Nov 6" was a false promise. The
 * month ends on the date and a plan is the way back; members ask an admin (D7).
 */
function planCanceling(b: Balance | null | undefined): boolean {
  return !!(b && b.plan && b.plan.cancelAtPeriodEnd);
}

/** "Your plan ends on Nov 6." (or "Your plan is ending." when the date is unknown). */
function planEndsLine(b: Balance): string {
  const d = b.plan ? shortDate(b.plan.currentPeriodEnd) : '';
  return d ? `Your plan ends on ${d}.` : 'Your plan is ending.';
}

/** The one sentence after a fact that only a plan fixes: admins pick, members ask (D7). */
function pickPlanTail(b: Balance | null | undefined): string {
  return b && !b.isAdmin ? COPY.askAdminPlan : 'Pick a plan to keep building.';
}

export function pauseTertiary(card: PauseCardData): string | null {
  const step = (card.stoppedStep || '').trim();
  return step ? `Paused while ${lowerFirst(step)}` : null;
}

/** The VISIBLE message the Continue button sends (D11), never a blind resend. */
export function continueMessage(card: Pick<PauseCardData, 'stoppedStep'>): string {
  const step = (card.stoppedStep || '').trim();
  return step ? `Continue where you stopped: ${lowerFirst(step)}` : 'Continue where you stopped.';
}

function lowerFirst(s: string): string {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/** Announced once through the card's polite live region. */
export function pauseAnnouncement(card: PauseCardData, b: Balance | null): string {
  return `${pauseEyebrow(card)}. ${pauseHeading(card, b)}. ${pauseBody(card, b)}`;
}

/**
 * "Link copied. Send it to Ada ..." — but only when the copy really worked.
 * With no clipboard (permission denied, non-HTTPS) the link itself is in the
 * sentence, so the user is never told they have something they don't.
 */
export function askAdminText(admins: string[], copiedUrl: string | null = null, copied = true): string {
  const names = (admins || []).filter(Boolean);
  const who = names.length === 0
    ? 'an admin on your team'
    : names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
  if (!copied && copiedUrl) return `Send this link to ${who} so they can add credit: ${copiedUrl}`;
  return `Link copied. Send it to ${who} so they can add credit.`;
}

export const COPY = {
  seePlans: 'See plans',
  // The credit meter's rows and its hover (review with Samuel, 2026-10-20):
  // the main row is the plan month ("Plan credits") or the free trial
  // ("Free credits"); everything else spendable is one "Extra credits" row,
  // and the hover names where each part came from. No plan name on the meter.
  planCredits: 'Plan credits',
  freeCredits: 'Free credits',
  extraCredits: 'Extra credits',
  usingPlan: 'Using plan credits',
  usingFree: 'Using free credits',
  usingExtra: 'Using extra credits',
  noCreditsLeft: 'No credits left',
  // Each kind of extra credit, in two words that say where it came from; a
  // sub-line only where a number needs explaining (what a top-up cost). Every
  // credit someone gave (a code, a link, staff) is "Promo credit".
  extraRollover: 'Rolled over',
  extraRolloverSub: 'from last month',
  extraTopup: 'Top-up',
  extraReferralEarned: 'Referral reward',
  extraReferralReceived: 'Friend invite',
  extraPromo: 'Promo credit',
  extraFree: 'Free credits',
  haveCode: 'I have a code',
  askAdmin: 'Ask an admin to add credit',
  askAdminPlan: 'Ask an admin to pick a plan.',
  contactUs: 'Contact us',
  checkAgain: 'Check again',
  continueStopped: 'Continue where it stopped',
  emailWhenLands: "We'll email you when it lands.",
  billingUnavailable: 'Billing is unavailable right now. Try again in a minute.',
  // 409 from Checkout: Stripe already has a live plan for this team, usually
  // one bought moments ago whose confirmation has not reached us yet.
  alreadySubscribed: 'This team already has a plan. It can take a minute to show here.',
  // 409 project_has_no_team: a legacy app never moved onto a team.
  projectHasNoTeam: "This app isn't on a team yet, so there's nothing to bill. Contact us to set one up.",
  plansUnavailable: 'There are no plans to pick on your account right now.',
  somethingWrong: 'Something went wrong. Try again in a minute.',
  composerHint: 'Add credits to keep building',
  composerHintAdmin: 'Ask an admin to add credits to keep building',
  // A build still running at $0 is spending the last of the credit (the
  // gateway's last call holds all of it): "add credits to keep building" beside
  // a working agent read as if it had stopped (founder, 2026-10-13). Short:
  // one line in the 400px task composer.
  composerHintLastCredit: 'Still building on your last credit',
  outOfCreditChat: "You're out of building credit. Add credit on the Plans page to keep going.",
  outOfCredit: "You're out of building credit.",
  outOfCreditMember: "You're out of building credit. Ask an admin to add credit to keep going.",
  planPublish: 'Publishing changes comes with a plan',
  planPublishFirst: 'Publishing comes with a plan',
  planPublishBody: 'Your live app stays up. A plan lets you publish your latest changes.',
  planPublishFirstBody: 'A plan puts your app on the internet with its own link.',
  // Redeem (state table row "Redeem")
  codeInvalid: "That code isn't valid.",
  codeAlready: 'That code is already on your account.',
  codeCovered: "Your team's plan already covers building.",
  codeLabel: 'Code',
  codePlaceholder: 'Enter a code',
  codeApply: 'Apply',
  codeChecking: 'Checking…',
  // Top-up sheet (decision 11)
  topupTitle: 'Add a top-up',
  topupOther: 'Other amount',
  topupMin: `$${10} minimum`,
  topupMax: "Up to $1,000 at a time",
  topupNote: 'Top-ups add building credit only and last 12 months. Credit that expires sooner is used first.',
  topupContinue: 'Continue to payment',
  topupOpening: 'Opening payment…',
  topupOpenFailed: "Couldn't open payment. Try again.",
  topupAddingNow: 'Payment received, adding it now…',
  topupStrip: 'Need more credit?',
  topupStripNote: 'Top-ups start at $10 and last 12 months.',
  topupStripButton: 'Add a top-up',
  topupNotMetered: "Your team's plan already covers building.",
  cardDeclined: 'Your card was declined. Update it in Manage billing, then try again.',
  hostingUnavailable: "We couldn't add hosting to your bill, so nothing was published. Try again.",
  hostingPriceUnavailable: "We couldn't check the hosting price, so nothing was published. Try again.",
  hostingConfirmAgain: 'The hosting price changed. Check it and confirm again to publish.',
  hostingPaymentFailed: 'Your card was declined, so no hosting slot was added and nothing was published. Update your card in Manage billing.',
  alwaysOnNotAllowed: "Always on needs a plan that includes app hosting. Nothing was published.",
  planChangeFailed: "We couldn't change your plan. Try again in a minute.",
  // Downgrade sheet (decision 12)
  downgradeTitle: 'Downgrade',
  downgradeNoteTail: 'Archived apps keep their code and data.',
  downgradeFull: 'Uncheck one to keep another.',
  downgradePublished: 'Published apps that are archived go offline.',
  publishedTag: 'Published',
  switching: 'Switching…',
  // Upgrade confirm (2026-10-07 local E2E: an upgrade charged on one click)
  upgrading: 'Upgrading…',
  notNow: 'Not now',
  upgradeCardOnFile: 'Charged to the card on file.',
  // Archive / restore by the owner (2026-10-07 local E2E: "Archive one" had no button)
  archivedTag: 'Archived',
  archiveApp: 'Archive app',
  restoreApp: 'Restore app',
  archiving: 'Archiving…',
  restoring: 'Restoring…',
  archivedComposer: 'This app is archived. Restore it to keep building.',
  archivedPreviewTitle: 'This app is archived',
  // Settings > Billing with hosting slots: the Stripe portal changes the card,
  // shows invoices and cancels; hosting slots change on /plans (their rules:
  // no refund, an in-use slot stays), and a cancel ends both.
  manageHosting: 'Manage hosting',
  portalScopeWithHosting: 'Manage billing: card, invoices and cancelling. Cancelling ends your plan and its app hosting together. Hosting slots are added and removed under Manage hosting.',
  archivedPreviewBody: 'Restore it to see the preview and keep building. Its code and data are kept.',
  // A template trial that ended with no plan cannot be restored yet: the way on is a plan.
  trialEndedComposer: 'Your template trial ended. Pick a plan to keep building.',
  trialEndedPreviewBody: 'Pick a plan with SaaS templates to see the preview and keep building. Its code and data are kept.',
  archiveFailed: "We couldn't archive this app, so nothing changed. Try again.",
  restoreFailed: "We couldn't restore this app. Try again in a minute.",
  // Lifecycle (decisions 6, 7, 8)
  planEndingCta: 'Update billing',
  restore: 'Restore',
  pickPlan: 'Pick a plan',
  // The server's in-flight states (review 2026-10-08): nothing to do but wait.
  restoringApp: 'Restoring your app… this can take a few minutes.',
  archivingApp: 'Archiving your app…',
  pausedPlanEnded: 'Paused because your plan ended',
  pausedTrialEnded: 'Paused because your trial ended',
  paused: 'Paused',
  archivedKept: 'Its code and data are kept.',
  archivedPlanEnded: 'Archived because your plan ended.',
  templateTrialAddCard: 'Try it free for 24 hours. We ask for a card to stop abuse. It is not charged.',
  templateTrialAddCardButton: 'Add a card',
  templateTrialOpening: 'Opening…',
  templateTrialReady: 'Starts your 24-hour trial. Publishing, downloads and the code come with Unlock.',
  templateTrialUsed: 'Your 24-hour template trial is used.',
  templateTrialCardSaved: 'Card saved. Use the template to start your 24-hour trial.',
  templateTrialEnded: 'Your 24-hour template trial ended. Pick a plan with SaaS templates to keep this app.',
  templateTrialCardFailed: "We couldn't open the card page. Try again.",
  templateTrialCancelled: 'No card was added, so nothing started. Add a card to try this template for 24 hours.',
  codeLockedOnTrial: 'Viewing the code comes with Unlock.',
  // Badge (decision 9)
  badgeRemovedOne: 'Badge removed from your live page.',
  badgeRemovedMany: 'Badge removed from your live pages.',
  // Plans page (decision 1)
  mostTeams: 'Popular',
  businessName: 'Business',
  businessPrice: 'Custom',
  bookCall: 'Book a call',
  talkToUs: 'Talk to us',
  choosePlan: 'Choose',
  // Checkout return (state table row "Checkout return")
  adding: 'Adding your credit…',
  addingBody: 'Your payment went through. This usually takes a few seconds.',
  added: 'Your plan is active. Your building credit is ready.',
  // Strips (payment failed; the add-email note). Sign-in is Google/GitHub
  // only and the provider vouches for the address, so there is no
  // "verify your email" step (2026-10-09). The note shows only for an account
  // with no address on file at all, which the free trial needs.
  addEmailStrip: 'Add your email address to start your free trial',
  addEmailCta: 'Add it in Settings',
  pastDueAdmin: "Your last payment didn't go through.",
  pastDueAdminCta: 'Update your card',
  pastDueMember: 'Billing needs attention. Ask an admin.',
  portalError: "Couldn't open billing. Try again.",
  noBillingOrgs: 'No teams to manage billing for.',
} as const;

/**
 * A spend refused where no pause card can render (a toast, the general chat).
 * Same rules as the card: members are pointed at an admin (D7), a subscribed
 * org is never pointed at plans (D9), and an unknown balance gets neither.
 */
export function outOfCreditNotice(
  b: Balance | null | undefined,
  projectId?: string | null,
): { text: string; plansHref: string | null } {
  if (!b) return { text: COPY.outOfCredit, plansHref: null };
  const plansHref = plansLink(PLANS_SRC.composerNotice, { project: projectId });
  if (isSubscribed(b)) {
    const used = "You've used this month's building credit.";
    if (planCanceling(b)) {
      // The month will not renew: the end date, and plans for whoever can pick one.
      return { text: `${used} ${planEndsLine(b)} ${pickPlanTail(b)}`, plansHref: b.isAdmin ? plansHref : null };
    }
    const d = b.plan ? shortDate(b.plan.currentPeriodEnd) : '';
    return { text: `${used}${d ? ` It renews on ${d}.` : ''}`, plansHref: null };
  }
  if (!b.isAdmin) return { text: COPY.outOfCreditMember, plansHref: null };
  return { text: COPY.outOfCreditChat, plansHref };
}

export function addedCredit(micros: number): string {
  return `Added ${formatUsdShort(micros)} of building credit`;
}

// ── Top-ups (P-TOPUP, design decision 11) ──────────────────────────────────

/** Mirrors server/config/plans.js TOPUP; the server is the one that refuses. */
export const TOPUP_PRESETS_USD = [10, 25, 50, 100] as const;
export const TOPUP_MIN_USD = 10;
export const TOPUP_MAX_USD = 1000;

export function isValidTopupUsd(usd: unknown): usd is number {
  return typeof usd === 'number' && Number.isInteger(usd) && usd >= TOPUP_MIN_USD && usd <= TOPUP_MAX_USD;
}

/** "Added $25 of top-up" (the success page, from the fulfil's amountCents). */
export function addedTopup(cents: number): string {
  const c = Math.max(0, Math.floor(cents || 0));
  return `Added ${c % 100 === 0 ? `$${c / 100}` : formatCents(c)} of top-up`;
}

/** The checkout-return banner: a plan or a top-up, from what the fulfil said. */
export function addedForFulfill(r: { kind?: string; amountCents?: number; badgeRepublished?: number; trial?: string | null; message?: string | null } | null | undefined): string {
  if (r && r.kind === 'topup') return addedTopup(r.amountCents || 0);
  // A template-trial card adds nothing: its own words, wherever it lands.
  const trial = templateTrialReturnText(r);
  if (trial) return trial.text;
  const n = (r && r.badgeRepublished) || 0;
  return n > 0 ? `${COPY.added} ${badgeRemovedLine(n)}` : COPY.added;
}

/** "Badge removed from your live page." (decision 9); plural for several pages. */
export const badgeRemovedLine = (pages: number): string => (pages === 1 ? COPY.badgeRemovedOne : COPY.badgeRemovedMany);
/** The billing tab says it for 30 days after the republish. */
export const BADGE_REMOVED_SHOW_MS = 30 * 24 * 60 * 60 * 1000;

/** "Add $10": the pause card's primary action when everything is at zero. */
export function addTopupLabel(usd: number = TOPUP_MIN_USD): string {
  return `Add $${usd}`;
}

/**
 * The next bigger plan in the catalog for a subscribed org ("Upgrade to
 * Pro" on a Starter pause card), or null at the top, on a trial, or when
 * the server sent no catalog (older server: nothing is invented).
 */
export function upgradeTarget(b: Balance | null | undefined): BillingOffer | null {
  if (!b || !isSubscribed(b) || !b.plan || !Array.isArray(b.catalog)) return null;
  const cur = b.catalog.find((o) => o.id === b.plan!.id);
  if (!cur) return null;
  const bigger = b.catalog.filter((o) => o.monthlyGrantMicros > cur.monthlyGrantMicros).sort((a, c) => a.monthlyGrantMicros - c.monthlyGrantMicros);
  return bigger[0] || null;
}

export const upgradeTo = (o: BillingOffer): string => `Upgrade to ${o.displayName}`;

// ── Refusal notes on the control (design decision 4, DT5) ──────────────────

export type RefusalFeature = 'createProject' | 'import' | 'saasTemplate' | 'zipDownload' | 'customDomains';

export interface RefusalNoteData {
  text: string;
  /** The ONE action; null for a member (D7) or when there is nothing to offer. */
  action: { label: string; href: string } | null;
  /** The template trial's "Add a card": a button that opens Stripe (setup
   *  mode, never charged) instead of a link. Only on the trial plan. */
  trialCard?: boolean;
}

/** Appends a query pair to a plans href that may already carry ?project=. */
function withQuery(href: string, key: string, value: string): string {
  return `${href}${href.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(value)}`;
}

/** The cheapest catalog plan that has `pred`, for "comes with Pro". */
function cheapestWhere(b: Balance, pred: (e: Entitlements) => boolean): BillingOffer | null {
  if (!Array.isArray(b.catalog)) return null;
  const hits = b.catalog.filter((o) => o.entitlements && pred(o.entitlements)).sort((x, y) => x.priceUsd - y.priceUsd);
  return hits[0] || null;
}

/**
 * What to show ON a control before the click (decision 4): the limit with
 * its count and one action, or null when the server would allow it. The
 * typed text is kept by the caller; the server still enforces. Says nothing
 * it cannot know: no balance, an older server without `limits`/`entitlements`,
 * gate off or shadow (the server allows then), unmetered -> null.
 */
export function refusalNote(b: Balance | null | undefined, feature: RefusalFeature, { plansHref: plansHrefIn = '/plans' }: { plansHref?: string } = {}): RefusalNoteData | null {
  if (!b || b.unmetered || b.gate !== 'enforce') return null;
  const admin = !!b.isAdmin;
  const ent = b.entitlements;
  const lim = b.limits;
  const subscribed = isSubscribed(b);
  // Every link out of a refusal says which control refused (lib/plansEntry).
  const plansHref = withPlansSrc(plansHrefIn, refusalSrc(feature));
  const seePlans = admin ? { label: COPY.seePlans, href: plansHref } : null;
  const planFor = (o: BillingOffer | null) => (o ? o.displayName : 'a plan');
  const upgradeAction = (o: BillingOffer | null) => (admin && o ? { label: upgradeTo(o), href: withQuery(plansHref, 'plan', o.id) } : null);

  const creates = feature === 'createProject' || feature === 'import' || feature === 'saasTemplate';
  if (creates) {
    // $0: nothing new starts, imports included (P-CAPS / P-TRIAL). A member
    // is never told to pay (D7): the limit, then the ask.
    if (isOut(b)) {
      const limit = subscribed ? "You're out of building this month." : "You're out of building.";
      if (!admin) return { text: `${limit} ${COPY.askAdmin}.`, action: null };
      // With no plan a top-up buys credit only: it gets nobody past the app cap
      // or a SaaS template, and the server would refuse them after they paid.
      const planNeeded = !subscribed && (
        (feature === 'saasTemplate' && !!ent && !ent.saasTemplates)
        || (!!lim && lim.projectCap != null && lim.activeProjects >= lim.projectCap)
      );
      if (canTopUp(b) && !planNeeded) {
        return { text: `${limit} Add $10 to start something new.`, action: { label: addTopupLabel(), href: withQuery(plansHref, 'topup', '1') } };
      }
      // A plan is what is needed, or an older server that says no top-up.
      return { text: `${limit} Pick a plan to start something new.`, action: seePlans };
    }
    if (lim && lim.projectCap != null && lim.activeProjects >= lim.projectCap) {
      // No plan (trial 3, founder 2026-10-13): the cheapest plan with room.
      const cap = lim.projectCap;
      const next = subscribed ? upgradeTarget(b) : cheapestWhere(b, (e) => e.projectCap == null || e.projectCap > cap);
      const text = `${lim.activeProjects} of ${lim.projectCap} active apps · ${next ? `Archive one or upgrade to ${next.displayName}` : 'Archive one to start another'}`;
      return { text, action: upgradeAction(next) };
    }
  }
  if (feature === 'saasTemplate') {
    if (!ent) return null;
    if (!ent.saasTemplates) {
      const o = cheapestWhere(b, (e) => !!e.saasTemplates);
      // The trial plan's one 24-hour template trial (founder 2026-10-13): a
      // card first (checked, never charged), then the start takes it. Older
      // server (no templateTrial): the plain line below.
      const tt = b.tier === 'trial' ? b.templateTrial : null;
      if (tt && tt.available && !tt.cardOnFile) return { text: COPY.templateTrialAddCard, action: null, trialCard: true };
      if (tt && tt.cardOnFile) return { text: COPY.templateTrialReady, action: null };
      if (tt && tt.used) return { text: `${COPY.templateTrialUsed} SaaS templates come with ${planFor(o)}`, action: seePlans };
      return { text: `SaaS templates come with ${planFor(o)}`, action: subscribed ? upgradeAction(o) : seePlans };
    }
    if (lim && lim.saasCap != null && lim.activeSaas >= lim.saasCap) {
      // A bigger plan with room for one more (Starter 1 -> Pro 3); none past Pro.
      const cap = lim.saasCap;
      const next = subscribed ? cheapestWhere(b, (e) => !!e.saasTemplates && (e.activeSaasLimit == null || e.activeSaasLimit > cap)) : null;
      // The catalog lists purchasable plans only, so Pro at its cap has no offer.
      const offer = next && next.id !== (b.plan && b.plan.id) ? next : null;
      const how = offer ? `Archive one or upgrade to ${offer.displayName}` : 'Archive one to start another';
      return { text: `${lim.activeSaas} of ${cap} SaaS apps in use · ${how} (archived apps keep their code)`, action: admin && offer ? upgradeAction(offer) : null };
    }
    return null;
  }
  if (feature === 'zipDownload') {
    if (!ent || ent.zipDownload !== false) return null;
    return { text: 'Download comes with a plan', action: seePlans };
  }
  if (feature === 'customDomains') {
    if (!ent || ent.customDomains !== false) return null;
    return { text: 'Custom domains come with a plan', action: seePlans };
  }
  return null;
}

// ── Hosting line and the Starter full-app hint (design decision 5, DT6) ────

/**
 * The Publish popover's hosting line for the FIRST publish of a hosted kind
 * on a plan (server hostingBillingService), by what that tier's slot costs:
 * a free slot the team owns costs nothing, a slot removed this period comes
 * back free until it renews, otherwise a new slot at the tier's price ($9
 * sleep when idle, $18 always on), charged now for the rest of the period.
 * Landing pages are static and never hosted; a trial cannot publish a full
 * app at all; the prices come from the server (balance.hosting).
 */
export function hostingLineFor(
  b: Balance | null | undefined,
  buildKind: string | null | undefined,
  tier: HostingTier = 'sleep',
  slots?: TierSlots | null,
): string | null {
  if (!b || b.unmetered || !isSubscribed(b) || !b.plan) return null;
  if (buildKind === 'landing' || buildKind === 'mobile') return null;
  const hosted = b.entitlements && Array.isArray(b.entitlements.hostedKinds) ? b.entitlements.hostedKinds : [];
  if (!hosted.includes('fullstack')) return null;
  const price = tierPrice(b, tier);
  const outcome = slots && slots.outcome ? slots.outcome[tier] : null;
  if (outcome === 'free') return `Uses your free ${TIER_NAME[tier]} slot. Nothing extra to pay.`;
  if (outcome === 'paid') {
    const until = shortDate(slots && slots.periodEnd);
    return `Uses ${aSlot(tier)} you've already paid for${until ? ` until ${until}` : ''}, then $${price}/month.`;
  }
  return `Adds $${price}/month to your ${b.plan.displayName} bill. Today you only pay for the days left this month.`;
}

const tierPrice = (b: Balance | null | undefined, tier: HostingTier): number => (tier === 'always-on'
  ? (b && b.hosting && b.hosting.alwaysOn && b.hosting.alwaysOn.priceUsd) || 18
  : (b && b.hosting && b.hosting.priceUsd) || 9);

/**
 * The tier switch confirm for a live app: what moving to `to` does to the
 * bill (server slot rules). The slot the app leaves stops renewing at the
 * period end; without slot data (an older server) the plain price swap.
 */
export function tierSwitchNote(
  prices: Partial<Record<HostingTier, number>> | null | undefined,
  from: HostingTier,
  to: HostingTier,
  slots?: TierSlots | null,
): string {
  if (!prices) return '';
  const outcome = slots && slots.outcome ? slots.outcome[to] : null;
  const end = shortDate(slots && slots.periodEnd);
  const leaves = `Your ${TIER_NAME[from]} slot stops renewing${end ? ` on ${end}` : ' at the end of the month'}.`;
  if (outcome === 'buy') return `Adds a $${prices[to]}/month ${TIER_NAME[to]} slot, charged today for the rest of this month. ${leaves}`;
  if (outcome === 'free') return `Uses your free ${TIER_NAME[to]} slot, nothing extra to pay. ${leaves}`;
  if (outcome === 'paid') return `Uses ${aSlot(to)} you've already paid for this month. ${leaves}`;
  if (outcome === 'covered') return `Nothing to pay today. ${end ? `From ${end}` : 'From next month'} hosting is $${prices[to]}/month instead of $${prices[from]}.`;
  return `Hosting becomes $${prices[to]}/month instead of $${prices[from]}.`;
}

export const switchTo = (o: BillingOffer): string => `Switch to ${o.displayName}`;
export const chooseLabel = (o: BillingOffer): string => `${COPY.choosePlan} ${o.displayName}`;
/**
 * After a plan change. A downgrade switches today and Stripe credits the
 * unused part of the bigger plan's month on the next invoice
 * (create_prorations); the sheet-less path (decision 12: no sheet when
 * nothing would be archived) never said so, so the money fact goes here
 * (design review 2026-10-08, FINDING-002).
 */
export const nowOnPlan = (o: BillingOffer, from?: BillingOffer | null): string => {
  const down = !!from && from.monthlyGrantMicros > o.monthlyGrantMicros;
  return down
    ? `You're on ${o.displayName} now. What you paid for the rest of this month on ${from!.displayName} comes off your next invoice.`
    : `You're on ${o.displayName} now.`;
};

export function rateLimited(retryAfterSec: number | null | undefined): string {
  const mins = Math.max(1, Math.ceil((retryAfterSec || 60) / 60));
  return `Too many tries. Try again in ${mins} min.`;
}

// ── Plans page (design D4, D16) ─────────────────────────────────────────────

export function offerPrice(o: BillingOffer): string {
  // Through cents like every other figure: 19.5 reads "$19.50", never "$19.5".
  return formatUsdShort(Math.round((Number(o.priceUsd) || 0) * 100) * 10_000);
}

export function offerInterval(o: BillingOffer): string {
  return `/ ${o.interval || 'month'}`;
}

/**
 * What a plan buys, without a dollar figure for the allowance (P-DISPLAY).
 * Pro is sold relative to Starter ("About 3x Starter's building"), Starter as
 * the baseline month.
 */
export function offerIncludes(o: BillingOffer, catalog: BillingOffer[] = []): string {
  const base = catalog.find((x) => x.id === 'starter');
  if (base && o.id !== base.id && base.monthlyGrantMicros > 0) {
    const ratio = Math.max(1, Math.round(o.monthlyGrantMicros / base.monthlyGrantMicros));
    return `${ratio}x the building credit`;
  }
  return 'Building credit every month';
}

/**
 * What the Business card lists. Plain words a ten-year-old can read (founder
 * 2026-10-14). No per-card taglines: the page lead says what plans are for.
 */
// From the calls: engineering support while you build (2026-10-08, 10-07),
// private/custom templates and team collaboration (priority builds were
// listed too but nothing queues Business first yet, so no line for it)
// (2026-09-11), a dedicated always-on machine per app (2026-10-06, 10-08),
// and the uptime figure (founder 2026-10-14).
export const BUSINESS_BULLETS: string[] = [
  'Real engineers build with you',
  'Priority support',
  'Always-on dedicated hosting',
  'Complex apps built for your workflow',
  'No limit on apps',
  // We build to the standard each customer must meet; no certificate is
  // claimed for the product itself (founder 2026-10-14).
  'Built to meet your compliance needs',
];

/**
 * The full-width Free card above the plan cards for a team with no paid
 * plan: the free trial or Free after it, said from the balance and its
 * entitlements (never a per-tier table), so the cards read as what you'd
 * gain. Trial credit is never said in dollars (pricing sheet: % only), only
 * when it ends. Null when subscribed, on a contract, or on an unknown tier.
 */
export function currentFreeTier(b: Balance | null | undefined): { name: string; onTrial: boolean; items: { text: string; tip?: string }[] } | null {
  if (!b || b.unmetered || isSubscribed(b)) return null;
  if (b.tier !== 'trial' && b.tier !== 'free') return null;
  // The free tier's headline offer, three lines only (founder 2026-10-14,
  // from the pricing sheet's "Free trial" column). Credit is never said in
  // dollars (sheet: % only).
  const cap = (b.entitlements && b.entitlements.projectCap) ?? 3;
  return {
    name: 'Free',
    // "(Your current plan)" only while the trial runs: after it, the team no
    // longer has what the row lists.
    onTrial: b.tier === 'trial',
    items: [
      { text: 'Building credit for 30 days' },
      { text: `${cap} active apps` },
      { text: 'Build landing pages and full stack apps' },
    ],
  };
}

/** The rollover card line and what its (i) explains on /plans. */
export const ROLLOVER_LINE = 'Unused credit rolls over';
export const ROLLOVER_TIP = "Credit you don't use this month rolls over to next month. Rolled-over credit lasts one more month, then expires.";

/**
 * Whether Publish should open on "comes with a plan" for this kind of app. A
 * free account publishes landing pages but not full apps, so "can publish
 * something" is not enough; without this its full app offers Publish, runs the
 * checks and only then is refused. The server enforces the same list.
 */
export function publishNeedsPlanFor(e: Partial<Entitlements> | null | undefined, buildKind: string | null | undefined): boolean {
  if (!e) return false;
  if (e.canPublish === false) return true;
  if (!Array.isArray(e.publishKinds)) return false;
  return !e.publishKinds.includes(buildKind === 'landing' ? 'landing' : 'fullstack');
}

/** The plan-card line /plans footnotes to the publishing prices under the cards. */
export const PUBLISH_LINE = 'Publish apps and landing pages';

/** One line a plan card can list, keyed so a bigger plan can drop what it shares. */
interface PlanLine { key: string; text: string }

function planLines(o: BillingOffer, catalog: BillingOffer[]): PlanLine[] {
  const e: Partial<Entitlements> = o.entitlements || {};
  const out: PlanLine[] = [{ key: 'credit', text: offerIncludes(o, catalog) }];
  // Plans bill the team, never the seat (company.md pillar: one price for
  // the whole company, not per user).
  out.push({ key: 'team', text: 'One bill for your whole team' });
  out.push({ key: 'apps', text: e.projectCap != null ? `${e.projectCap} active apps` : 'No limit on apps' });
  if (e.saasTemplates) {
    const n = e.activeSaasLimit;
    out.push({ key: 'templates', text: n == null ? 'All replacement templates' : n === 1 ? '1 replacement template' : `${n} replacement templates` });
  }
  if (e.customDomains) out.push({ key: 'domain', text: 'Use your own domain' });
  const kinds = e.publishKinds || [];
  // "Publish" is the word on the app's button. The full-app line carries a
  // footnote marker on /plans (PUBLISH_LINE) tying it to the publishing
  // prices under the cards; landing pages alone are free, so no marker.
  if (kinds.includes('fullstack')) out.push({ key: 'publish', text: PUBLISH_LINE });
  else if (kinds.includes('landing')) out.push({ key: 'publish', text: 'Publish landing pages' });
  // Published pages carry the "Built with Founding.dev" badge on the trial
  // (decision 9); a plan that publishes without it says so.
  if (kinds.length > 0 && e.badge === false) out.push({ key: 'badge', text: 'Remove Founding.dev badge' });
  // Rollover (P-ROLL) holds on every plan, one month (founder 2026-10-14:
  // "rolls over"; "carries over" stays rejected, copyLint enforces it). The
  // card line stays short; its (i) on /plans says "to next month, for one
  // month" (ROLLOVER_LINE / ROLLOVER_TIP).
  if (o.monthlyGrantMicros > 0) out.push({ key: 'extra', text: ROLLOVER_LINE });
  return out;
}

/** The next plan down in the catalog (less building), or null for the smallest. */
function planBelow(o: BillingOffer, catalog: BillingOffer[]): BillingOffer | null {
  const below = catalog.filter((x) => x.id !== o.id && x.monthlyGrantMicros < o.monthlyGrantMicros);
  return below.length ? below.reduce((a, b) => (b.monthlyGrantMicros > a.monthlyGrantMicros ? b : a)) : null;
}

/**
 * What a plan card lists, from its entitlements (never hard-coded per plan).
 * A bigger plan says "Everything in Unlock, plus:" and lists only what
 * changes, so no line repeats across cards. Hosting is said once, under the
 * cards (HostingOffer), not on each card.
 */
export function planCard(o: BillingOffer, catalog: BillingOffer[] = []): { lead: string | null; bullets: string[] } {
  const lines = planLines(o, catalog);
  const base = planBelow(o, catalog);
  if (!base) return { lead: null, bullets: lines.map((l) => l.text) };
  const had = new Map(planLines(base, catalog).map((l) => [l.key, l.text]));
  return {
    lead: `Everything in ${base.displayName}, plus:`,
    bullets: lines.filter((l) => had.get(l.key) !== l.text).map((l) => l.text),
  };
}

/** Every line a plan unlocks, shared lines included. */
export function planBullets(o: BillingOffer, catalog: BillingOffer[] = []): string[] {
  return planLines(o, catalog).map((l) => l.text);
}

/** No plan, the other credit spent, a top-up carrying on: its percent and date say nothing they still hold. */
export function onlyTopupLeft(b: Balance): boolean {
  const u = b.usage;
  return !isSubscribed(b) && !!u && u.planPctUsed != null && u.planPctUsed >= 100 && (u.topupRetailCents || 0) > 0;
}

/**
 * The line under the /plans title (decision 1, reworded by Aima 2026-10-14):
 * paid -> what the plan is for (the "Building plan" card carries the usage);
 * free trial or nothing yet -> "Pick a plan to ..."; building credit ->
 * "Building credit: 26% used · ends Oct 28"; or how it ended.
 */
export function plansLead(b: Balance | null): string {
  if (!b) return '';
  const u = b.usage;
  if (isFreeExpired(b) && !(u && (u.topupRetailCents || 0) > 0)) {
    const d = b.expiredAt ? shortDate(b.expiredAt) : '';
    return expiredWasFree(b) ? `Your free trial ended${d ? ` on ${d}` : ''}` : `Your building credit ended${d ? ` on ${d}` : ''}`;
  }
  // Paid: the "Building plan" card says the plan, usage and renewal; the
  // lead says what the plan is for (Aima, 2026-10-14).
  if (isSubscribed(b)) return 'Replace expensive software. Build apps and landing pages';
  // On the free trial (or nothing yet): the ask, not the trial's usage.
  if (!u || u.planPctUsed == null || u.basis === 'trial') {
    return (u && (u.topupRetailCents || 0) > 0) ? `${formatCents(u.topupRetailCents)} of top-up left` : 'Pick a plan to replace expensive software or build apps and landing pages';
  }
  // Only the top-up is left: the date below would read as the top-up's.
  if (onlyTopupLeft(b)) return `${formatCents(u.topupRetailCents)} of top-up left`;
  const who = u.basis === 'credit' ? 'Building credit' : (b.plan ? b.plan.displayName : 'Plan');
  const endShort = shortDate(periodEndOf(b));
  const parts = [`${who}: ${Math.max(0, Math.min(100, u.planPctUsed))}% used`, ...extraParts(u)];
  if (endShort) parts.push(`ends ${endShort}`);
  return parts.join(' · ');
}

/**
 * The detail beside the plan's name on the /plans "Building plan" card, which
 * is the only place a subscribed team's usage shows on that page, in the
 * credit meter's words: "62% of plan credits left · +79% extra credits ·
 * renews Nov 14". Empty when there is nothing to say.
 */
export function planCardDetail(b: Balance): string {
  const p = b.plan;
  const u = b.usage;
  const parts: string[] = [];
  if (u && u.planPctUsed != null) {
    const used = Math.max(0, Math.min(100, u.planPctUsed));
    parts.push(used < 100 ? `${100 - used}% of plan credits left` : 'plan credits used up');
  }
  parts.push(...extraParts(u));
  const date = p ? shortDate(p.currentPeriodEnd) : '';
  if (date) parts.push(`${p!.cancelAtPeriodEnd ? 'ends' : 'renews'} ${date}`);
  return parts.join(' · ');
}

// ── Billing tab (Profile) ───────────────────────────────────────────────────

export function billingTabLine(b: Balance): { line: string; warning: string | null } {
  const p = b.plan;
  const u = b.usage;
  const pct = u && u.planPctUsed != null ? `${Math.max(0, Math.min(100, u.planPctUsed))}% used` : null;
  if (p && isSubscribed(b)) {
    const date = shortDate(p.currentPeriodEnd);
    const line = [p.displayName, pct, date ? `${p.cancelAtPeriodEnd ? 'ends' : 'renews'} ${date}` : null].filter(Boolean).join(' · ');
    const warning = p.status === 'past_due'
      ? "Your last payment didn't go through. Update your card in Manage billing."
      : p.cancelAtPeriodEnd
        ? 'Your plan ends on that date. You can resume it in Manage billing.'
        : null;
    return { line, warning };
  }
  if (isFreeExpired(b) && !(u && (u.topupRetailCents || 0) > 0)) {
    const d = b.expiredAt ? shortDate(b.expiredAt) : '';
    return { line: expiredWasFree(b) ? `Free trial ended${d ? ` on ${d}` : ''}` : `Building credit ended${d ? ` on ${d}` : ''}`, warning: null };
  }
  const exp = shortDate(periodEndOf(b));
  const who = u && u.basis === 'trial' ? 'Free trial' : creditLabel(b);
  if (!pct || onlyTopupLeft(b)) {
    const topup = u && (u.topupRetailCents || 0) > 0 ? `${formatCents(u.topupRetailCents)} of top-up left` : null;
    return { line: [who, topup].filter(Boolean).join(' · '), warning: null };
  }
  return { line: [who, pct, exp ? `ends ${exp}` : null].filter(Boolean).join(' · '), warning: null };
}

/** "Oct 2027" for "lasts until". */
export function monthYear(iso: string | Date | null | undefined): string {
  const d = toDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
}

/**
 * The extra lines under the billing figure (admins): the top-up left and the
 * hosting line, each only when there is one.
 */
export function billingTabLines(b: Balance, now: number = Date.now()): string[] {
  const out: string[] = [];
  const u = b.usage;
  if (u && (u.topupRetailCents || 0) > 0) {
    const until = monthYear(u.topupExpiresAt);
    out.push(`Top-up ${formatCents(u.topupRetailCents)}${until ? ` · lasts until ${until}` : ''}`);
  }
  // One line per hosting tier once an app is always on: the count and the
  // price are each line's own (one sleep + one always-on app read
  // "Hosting × 1 · $27/month" when the sleep count carried both prices, and
  // an org with only always-on apps saw no hosting line at all).
  const h = b.hosting;
  const ao = h && h.alwaysOn && h.alwaysOn.quantity > 0 ? h.alwaysOn : null;
  if (h && h.slots) {
    out.push(...hostingSlotLines(h.slots));
  } else if (h && ao) {
    if (h.quantity > 0) out.push(`Hosting · Sleep when idle × ${h.quantity} · $${h.quantity * h.priceUsd}/month`);
    out.push(`Hosting · Always on × ${ao.quantity} · $${ao.quantity * ao.priceUsd}/month`);
  } else if (h && h.quantity > 0) {
    out.push(`Hosting × ${h.quantity} · $${Math.round((h.monthlyCents || h.quantity * h.priceUsd * 100) / 100)}/month`);
  }
  const bd = b.badge;
  if (bd && bd.removedAt && now - new Date(bd.removedAt).getTime() < BADGE_REMOVED_SHOW_MS && now >= new Date(bd.removedAt).getTime() - 60_000) {
    out.push(badgeRemovedLine(bd.pages || 1).replace(/\.$/, ''));
  }
  return out;
}

/** Does the team hold (or still have ending) hosting slots? */
export function holdsHostingSlots(b: Balance | null | undefined): boolean {
  const slots = b && b.hosting && b.hosting.slots;
  if (slots) return (['sleep', 'always-on'] as HostingTier[]).some((t) => !!slots[t] && (slots[t].owned > 0 || slots[t].ending > 0));
  const h = b && b.hosting;
  return !!h && ((h.quantity || 0) > 0 || !!(h.alwaysOn && h.alwaysOn.quantity > 0));
}

/**
 * One line per hosting tier the team holds slots of: "Hosting · Sleep when
 * idle × 2 · $18/month · 1 free", plus " · 1 ends Oct 28" for slots removed
 * this period. A tier with no slots has no line.
 */
export function hostingSlotLines(slots: HostingSlots): string[] {
  const out: string[] = [];
  const end = shortDate(slots.periodEnd);
  for (const t of ['sleep', 'always-on'] as HostingTier[]) {
    const v = slots[t];
    if (!v || (v.owned <= 0 && v.ending <= 0)) continue;
    if (v.owned <= 0) {
      out.push(`Hosting · ${TIER_NAME[t]} · ${v.ending === 1 ? '1 slot ends' : `${v.ending} slots end`}${end ? ` ${end}` : ''}`);
      continue;
    }
    const parts = [`Hosting · ${TIER_NAME[t]} × ${v.owned}`, `$${v.owned * v.priceUsd}/month`];
    const renewingFree = Math.max(0, v.free - v.ending);
    if (renewingFree > 0) parts.push(`${renewingFree} free`);
    if (v.ending > 0) parts.push(`${v.ending} ${v.ending === 1 ? 'ends' : 'end'}${end ? ` ${end}` : ''}`);
    out.push(parts.join(' · '));
  }
  return out;
}

// ── Errors from spend / plan routes ─────────────────────────────────────────

/** 402 {code:'credits_exhausted', card} from a spend route (design D8). */
export interface CreditsExhaustedError extends Error {
  code: 'credits_exhausted';
  status: 402;
  card: PauseCardData | null;
}

export function makeCreditsExhaustedError(card: PauseCardData | null): CreditsExhaustedError {
  const e = new Error("You're out of building credit.") as CreditsExhaustedError;
  e.code = 'credits_exhausted';
  e.status = 402;
  e.card = card || null;
  return e;
}

/** Recognises both our thrown error and a raw axios 402. */
export function creditsExhaustedCard(err: any): { hit: boolean; card: PauseCardData | null } {
  if (!err) return { hit: false, card: null };
  if (err.code === 'credits_exhausted') return { hit: true, card: err.card || null };
  const data = err.response && err.response.data;
  if (err.response && err.response.status === 402 && data && data.code === 'credits_exhausted') {
    return { hit: true, card: data.card || null };
  }
  return { hit: false, card: null };
}

/**
 * A sentence for ANY API error, never an object. Spend and plan refusals
 * (402) carry `error: {type, message}`, not a string; passing that straight
 * to a toast rendered "[object Object]" or crashed the toast.
 */
export function apiErrorText(err: any, fallback: string): string {
  const e = err && err.response && err.response.data && err.response.data.error;
  if (typeof e === 'string' && e) return e;
  if (e && typeof e.message === 'string' && e.message) return e.message;
  return fallback;
}

export function isPlanRequired(err: any): boolean {
  if (!err) return false;
  if (err.code === 'plan_required') return true;
  const r = err.response;
  return !!(r && r.status === 402 && r.data && r.data.code === 'plan_required');
}

/**
 * A relative, same-origin return path, or null. A weaker pre-check than the
 * server's safeReturnPath (routes/billing.js), which also caps length and
 * normalises through URL parsing and is the real open-redirect guard.
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  return raw;
}

// ── Downgrade (decision 12, T10 / DT9) ───────────────────────────────────────

/** One active app in the downgrade picker (server GET /api/billing/change-plan/preview). */
export interface DowngradePreviewProject {
  id: string;
  name: string;
  lastActivityAt: string | null;
  published: boolean;
  /** A SaaS-template app: it cannot run on a plan without SaaS, so it is archived, never kept. */
  saas: boolean;
}

export interface ChangePlanPreview {
  planId: string;
  /** The target plan's active-project cap; null = no cap. */
  cap: number | null;
  saasAllowed: boolean;
  projects: DowngradePreviewProject[];
  /** The server's pick (most recently used first, no SaaS), the same order it archives by. */
  defaultKeep: string[];
  /** Something would be archived: more apps than the cap, or SaaS apps on a plan without SaaS. */
  over: boolean;
  /** An upgrade's charge today, from Stripe's own preview; null for a downgrade or when Stripe could not say. */
  charge?: UpgradeCharge | null;
}

/** What an upgrade charges today (server stripeService.upgradeCharge). */
export interface UpgradeCharge {
  /** In the currency's minor unit (cents for USD). */
  amountDueMinor: number;
  currency: string;
  /** Seconds; sent back with the upgrade so Stripe bills exactly this amount. */
  prorationDate: number;
  nextAmountMinor: number | null;
  nextAt: string | null;
}

/**
 * What publishing (or switching) an app on a hosting tier charges now, from
 * Stripe's proration preview (GET /billing/hosting/preview). `card` is null
 * for non-admins or when Stripe has none on file.
 */
export type HostingChargePreview =
  | { charge: false }
  | {
    charge: true;
    tier: HostingTier;
    units: number;
    amountDueMinor: number;
    currency: string;
    /** Seconds; sent back with the publish so Stripe bills exactly this amount. */
    prorationDate: number;
    monthlyMinor: number;
    newMonthlyMinor: number;
    periodEnd: string | null;
    card: { brand: string; last4: string } | null;
  };
export type HostingCharge = Extract<HostingChargePreview, { charge: true }>;

/** The customer's yes to a hosting charge, carried by the publish request. */
export interface ChargeConfirmation {
  tier: HostingTier;
  prorationDate: number;
  amountDueMinor: number;
}

export const chargeConfirmation = (c: HostingCharge): ChargeConfirmation => ({ tier: c.tier, prorationDate: c.prorationDate, amountDueMinor: c.amountDueMinor });

const CARD_BRAND: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay' };

/** The hosting confirm sheet's words (Aima, 2026-10-14). `action`: a first publish or a live app's tier switch. */
export function hostingChargeCopy(c: HostingCharge, { appName, planName, action }: { appName: string; planName: string | null; action: 'publish' | 'switch' }) {
  const money = (m: number) => minorMoney(m, c.currency);
  const end = c.periodEnd ? shortDate(c.periodEnd) : '';
  const today = shortDate(new Date().toISOString());
  const monthly = `${money(c.monthlyMinor)}/month`;
  const due = c.amountDueMinor > 0 ? money(c.amountDueMinor) : null;
  const bill = planName ? `your ${planName} bill` : 'your bill';
  return {
    title: `Confirm hosting for ${appName}`,
    lead: action === 'switch'
      ? `Switching ${appName} to ${TIER_NAME[c.tier]} adds its hosting to ${bill}.`
      : `Publishing puts ${appName} online. Its hosting is added to ${bill}.`,
    hostingRow: `${TIER_NAME[c.tier]} hosting`,
    hostingValue: monthly,
    totalRow: 'New monthly total',
    totalValue: `${money(c.newMonthlyMinor)}/month`,
    dueRow: end && today ? `Due today (${today} to ${end})` : 'Due today',
    dueValue: due || money(0),
    card: c.card ? `${CARD_BRAND[c.card.brand] || 'Card'} •••• ${c.card.last4}` : null,
    cardFallback: "Charged to your team's card on file.",
    consent: due
      ? `I agree to pay ${due} today and ${monthly}${end ? ` from ${end}` : ''} for this hosting.`
      : `I agree to pay ${monthly}${end ? ` from ${end}` : ''} for this hosting.`,
    fine: 'It renews with your plan. To stop paying, unpublish the app and remove its slot on Plans.',
    pay: due ? `Pay ${due} and ${action === 'switch' ? 'switch' : 'publish'}` : action === 'switch' ? 'Switch' : 'Publish',
    cancel: 'Cancel',
    declinedTitle: 'Your card was declined',
    declinedBody: action === 'switch' ? 'Nothing was charged and nothing changed.' : 'Nothing was charged and nothing was published.',
    updateCard: 'Update card',
  };
}

/** "$23.33", "$50": an amount in a currency's minor unit, whole amounts without cents. */
export function minorMoney(amountMinor: number, currency: string = 'usd'): string {
  const code = (currency || 'usd').toUpperCase();
  let digits = 2;
  try { digits = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits ?? 2; } catch { return `$${(amountMinor / 100).toFixed(2)}`; }
  const value = amountMinor / 10 ** digits;
  const whole = Number.isInteger(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: whole ? 0 : digits, maximumFractionDigits: digits }).format(value);
}

/**
 * The upgrade confirm's sentence: what is charged today and what renews.
 * "You pay $23.33 today for the rest of this month. Then $59 a month from Nov 6."
 */
export function upgradeChargeText(target: BillingOffer, charge: UpgradeCharge | null | undefined): string {
  const nextMinor = charge && charge.nextAmountMinor != null ? charge.nextAmountMinor : target.priceUsd != null ? Math.round(target.priceUsd * 100) : null;
  const next = nextMinor != null ? minorMoney(nextMinor, charge && charge.nextAmountMinor != null ? charge.currency : 'usd') : null;
  const from = charge && charge.nextAt ? ` from ${shortDate(charge.nextAt)}` : '';
  const then = next ? ` Then ${next} a month${from}.` : '';
  if (!charge) return `You pay the difference for the rest of this month today.${then}`;
  if (charge.amountDueMinor <= 0) return `Nothing to pay today.${then}`;
  return `You pay ${minorMoney(charge.amountDueMinor, charge.currency)} today for the rest of this month.${then}`;
}

/** The confirm button: the amount when there is one. */
export function upgradePayLabel(target: BillingOffer, charge: UpgradeCharge | null | undefined): string {
  return charge && charge.amountDueMinor > 0 ? `Upgrade and pay ${minorMoney(charge.amountDueMinor, charge.currency)}` : upgradeTo(target);
}

/** The one mapping of a change-plan failure to words (the page and the sheet agree). */
export function planChangeErrorText(err: any): string {
  const status = err?.response?.status;
  if (status === 402) return COPY.cardDeclined;
  if (status === 503) return COPY.billingUnavailable;
  if (status === 403) return COPY.askAdmin;
  return COPY.planChangeFailed;
}

export const downgradeKeeps = (o: BillingOffer, cap: number): string =>
  `${o.displayName} keeps ${cap} active ${cap === 1 ? 'app' : 'apps'}.`;
export const archivedOn = (o: BillingOffer): string => `Archived on ${o.displayName}`;
export const downgradeNote = (current: BillingOffer | null): string =>
  current ? `${COPY.downgradeNoteTail} Restore them on ${current.displayName} anytime.` : COPY.downgradeNoteTail;
export const keptCount = (n: number, cap: number): string => `${n} of ${cap} kept`;
export const lastUsed = (iso: string | null | undefined): string => (iso ? `Last used ${shortDate(iso)}` : 'Not used yet');

// ── Lifecycle (decisions 6, 7, 8; T11 / DT7) ─────────────────────────────────

/**
 * Mirrors PLAN_ENDING_STATUSES in server/services/projectLifecycleService.js
 * (the set the pause dates and warning emails key on); change both together.
 */
export const PLAN_ENDING_STATUSES: ReadonlySet<string> = new Set(['past_due', 'canceled', 'unpaid', 'incomplete_expired', 'paused']);

/** A plan that is on its way out: canceling at period end, past due, or already ended. */
export function planEnding(b: Balance | null | undefined): boolean {
  const p = b && b.plan;
  if (!p) return false;
  return !!p.cancelAtPeriodEnd || PLAN_ENDING_STATUSES.has(p.status);
}

/** "Your apps pause on Nov 5 unless your plan continues" (decision 8), or null. */
export function planEndingLine(b: Balance | null | undefined): string | null {
  const at = b && b.lifecycle && b.lifecycle.appsPauseAt;
  if (!at || !planEnding(b)) return null;
  return `Your apps pause on ${shortDate(at)} unless your plan continues`;
}

/** "Your page stays live until Dec 6 unless you pick a plan." (decision 6), or null. */
export function pagePauseLine(b: Balance | null | undefined): string | null {
  const at = b && b.lifecycle && b.lifecycle.pagePauseAt;
  if (!at || isSubscribed(b)) return null;
  return `Your page stays live until ${shortDate(at)} unless you pick a plan.`;
}

export interface LifecycleBannerData {
  text: string;
  tone: 'warn' | 'info';
  /** A link (to /plans), or `restore: true` for the in-place Restore button. */
  action: { label: string; href?: string; restore?: boolean } | null;
}

/**
 * server/models/Project.js lifecycle.state. `restoring` and `archiving` are
 * the in-flight states (a restore redeploys; an archive tears down): the
 * project is busy, and Archive / Restore are disabled while it is.
 */
export type LifecycleState = 'live' | 'paused' | 'archived' | 'restoring' | 'archiving';

/** Busy: a restore or archive is in flight, so callers disable both actions. */
export function lifecycleBusy(state: LifecycleState | string | null | undefined): boolean {
  return state === 'restoring' || state === 'archiving';
}

/**
 * The owner's banner on a paused or archived project (decision 7). Admins
 * get the one action that brings it back; members are pointed at an admin
 * (D7). Live projects get nothing; a busy one gets the fact and no action.
 *
 * The action is what REALLY brings it back (review 2026-10-08): paused or
 * archived because a plan or trial ended -> "See plans" (only a plan
 * restores it; "Restore" there was a lie). Archived by the owner or over a
 * downgrade cap -> "Restore", in place.
 */
export function lifecycleBanner(
  project: { lifecycle?: { state?: LifecycleState | string; pauseReason?: string | null; archiveReason?: string | null } | null } | null | undefined,
  b: Balance | null | undefined,
  { plansHref = '/plans' }: { plansHref?: string } = {},
): LifecycleBannerData | null {
  const lc = project && project.lifecycle;
  if (!lc || !lc.state || lc.state === 'live') return null;
  if (lc.state === 'restoring') return { text: COPY.restoringApp, tone: 'info', action: null };
  if (lc.state === 'archiving') return { text: COPY.archivingApp, tone: 'info', action: null };
  const admin = !!(b && b.isAdmin);
  // "See plans" says why the app is paused or archived (lib/plansEntry).
  const act = (label: string, href = withPlansSrc(plansHref, lifecycleSrc(lc.state === 'paused' ? lc.pauseReason : lc.archiveReason))) => (admin ? { label, href } : null);
  const tail = admin ? '' : ` ${COPY.askAdminPlan}`;
  if (lc.state === 'paused') {
    if (lc.pauseReason === 'trial_ended') return { text: `${COPY.pausedTrialEnded}.${tail}`, tone: 'warn', action: act(COPY.seePlans) };
    if (lc.pauseReason === 'plan_ended') return { text: `${COPY.pausedPlanEnded}.${tail}`, tone: 'warn', action: act(COPY.seePlans) };
    return { text: `${COPY.paused}.${tail}`, tone: 'warn', action: act(COPY.restore) };
  }
  // archived
  // Archived over a cap (a downgrade) or by the owner: Restore, in place. It
  // takes a slot again, so a full plan answers with its limit (restoreErrorText).
  const restore = admin ? { label: COPY.restore, restore: true } : null;
  if (lc.archiveReason === 'downgrade') {
    const cur = b && b.plan ? b.plan.displayName : null;
    return { text: `${cur ? `Archived on ${cur}.` : 'Archived.'} ${COPY.archivedKept}${tail}`, tone: 'info', action: restore };
  }
  if (lc.archiveReason === 'user') {
    return { text: `Archived. ${COPY.archivedKept}${tail}`, tone: 'info', action: restore };
  }
  // Archived because a trial or plan ended, and the org is on a plan again:
  // Restore, in place (a full plan answers with its limit). Before this the
  // banner kept saying "pick a plan" to someone who had just bought one
  // (2026-10-13 E2E).
  const onPlan = !!(b && (b.plan || b.unmetered));
  if (onPlan && (lc.archiveReason === 'template_trial_ended' || lc.archiveReason === 'plan_ended' || lc.archiveReason === 'trial_ended')) {
    return { text: `Archived. ${COPY.archivedKept}${tail}`, tone: 'info', action: restore };
  }
  if (lc.archiveReason === 'template_trial_ended') {
    return { text: `${COPY.templateTrialEnded} ${COPY.archivedKept}${tail}`, tone: 'info', action: act(COPY.seePlans) };
  }
  if (lc.archiveReason === 'plan_ended' || lc.archiveReason === 'trial_ended') {
    return { text: `${COPY.archivedPlanEnded} ${COPY.archivedKept}${tail}`, tone: 'info', action: act(COPY.seePlans) };
  }
  return { text: `Archived. ${COPY.archivedKept}${tail}`, tone: 'info', action: null };
}

/**
 * Why a restore was refused, in the plan's words. A restore takes an active
 * slot again, so the refusals are the create refusals, said about THIS app.
 */
export function restoreErrorText(err: any, b?: Balance | null): string {
  const status = err?.response?.status;
  const d = err?.response?.data || {};
  if (status === 402 && d.code === 'plan_required') {
    const lim = d.limit as { used?: number; cap?: number } | null;
    const catalog = b ? (Array.isArray(b.catalog) && b.catalog.length ? b.catalog : b.offer ? [b.offer] : []) : [];
    const next = d.requiredPlanId ? (catalog.find((o) => o.id === d.requiredPlanId) || null) : null;
    const up = next ? ` or upgrade to ${next.displayName}` : '';
    if (d.kind === 'saas' && lim && lim.cap != null) return `${lim.used} of ${lim.cap} SaaS apps are in use. Archive one${up} to restore this app.`;
    if (d.kind === 'saas') return `SaaS templates come with ${next ? next.displayName : 'Unlock'}.`;
    if (d.feature === 'publish') return `Publishing this app comes with ${next ? next.displayName : 'a plan'}.`;
    if (lim && lim.cap != null) return `${lim.used} of ${lim.cap} active apps are in use. Archive one${up} to restore this app.`;
    return d.error?.message || COPY.restoreFailed;
  }
  if (status === 403) return COPY.askAdminPlan;
  if (status === 409 && typeof d.error === 'string') return d.error;
  return COPY.restoreFailed;
}

export function archiveErrorText(err: any): string {
  const status = err?.response?.status;
  const d = err?.response?.data || {};
  if (status === 403) return COPY.askAdminPlan;
  if (status === 409 && typeof d.error === 'string') return d.error;
  return COPY.archiveFailed;
}

/** The archive confirm's words: what stops, what stays. */
export function archiveConfirmText(name: string, { goesOffline = false }: { goesOffline?: boolean } = {}): string {
  return `Archive ${name}? It stops counting toward your active apps. Its code and data are kept, and you can restore it anytime.${goesOffline ? ' Its live site goes offline until you restore it.' : ''}`;
}

// ── The 24-hour template trial (founder 2026-10-13) ────────────────────────

/** "5h left", "45m left", "Ends now": the time left on a trial, rounded down. */
export function trialTimeLeft(endsAt: string | Date | null | undefined, now: number = Date.now()): string | null {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - now;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 60_000) return 'Ends now';
  const h = Math.floor(ms / 3_600_000);
  if (h >= 1) return `${h}h left`;
  return `${Math.floor(ms / 60_000)}m left`;
}

/**
 * The task page's line on a template-trial app while its org has no plan:
 * "Template trial · 5h left · Publishing and the code come with Starter".
 * Null on any other app, on a plan (the app is an ordinary SaaS app then),
 * once the 24h are over (the archived banner says that), or without the
 * gate enforcing (nothing is locked then).
 */
export function templateTrialBanner(
  project: { templateTrial?: { endsAt?: string | null } | null; lifecycle?: { state?: string } | null } | null | undefined,
  b: Balance | null | undefined,
  { plansHref = '/plans', now = Date.now() }: { plansHref?: string; now?: number } = {},
): { text: string; action: { label: string; href: string } | null } | null {
  const endsAt = project && project.templateTrial && project.templateTrial.endsAt;
  if (!endsAt || !b || b.unmetered || b.gate !== 'enforce' || b.plan) return null;
  if (project && project.lifecycle && project.lifecycle.state && project.lifecycle.state !== 'live') return null;
  if (new Date(endsAt).getTime() <= now) return null;
  const left = trialTimeLeft(endsAt, now);
  const o = cheapestWhere(b, (e) => !!e.saasTemplates);
  return {
    text: `Template trial · ${left} · Publishing, downloads and the code come with ${o ? o.displayName : 'Unlock'}`,
    action: b.isAdmin ? { label: COPY.seePlans, href: withPlansSrc(plansHref, PLANS_SRC.templateTrialBanner) } : null,
  };
}

/**
 * A template-trial app while its org has no plan (gate enforcing): the app is
 * seen in preview and changed through chat only (founder 2026-10-13), so the
 * code views (Code, Code Changes, Terminal) are not offered. The server
 * refuses the code routes anyway (402 codeView); this keeps the UI honest.
 */
export function templateTrialCodeLocked(
  project: { templateTrial?: { endsAt?: string | null } | null } | null | undefined,
  b: Balance | null | undefined,
): boolean {
  if (!project || !project.templateTrial || !project.templateTrial.endsAt) return false;
  return !!b && !b.unmetered && b.gate === 'enforce' && !b.plan;
}

/**
 * The archived app's composer and preview words. An app archived because its
 * 24-hour template trial ended, on an org with no plan, says to pick a plan
 * (Restore needs one); every other archived app says Restore.
 */
export function archivedCopy(
  project: { lifecycle?: { archiveReason?: string | null } | null } | null | undefined,
  b: Balance | null | undefined,
): { composer: string; previewBody: string } {
  const trialEnded = !!project && !!project.lifecycle && project.lifecycle.archiveReason === 'template_trial_ended'
    && !!b && !b.unmetered && !b.plan;
  return trialEnded
    ? { composer: COPY.trialEndedComposer, previewBody: COPY.trialEndedPreviewBody }
    : { composer: COPY.archivedComposer, previewBody: COPY.archivedPreviewBody };
}

/** What the Templates page says when Stripe sends the person back from the trial's card page. */
export function templateTrialReturnText(r: { kind?: string; trial?: string | null; message?: string | null } | null | undefined): { text: string; ok: boolean } | null {
  if (!r || r.kind !== 'template_trial') return null;
  if (r.trial === 'card') return { text: COPY.templateTrialCardSaved, ok: true };
  if (r.trial === 'used') return { text: COPY.templateTrialUsed, ok: false };
  if (r.trial === 'refused') return { text: r.message || COPY.templateTrialCardFailed, ok: false };
  return null;
}
